import { useMemo, useRef, useEffect } from 'react'
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, TimeScale, Filler } from 'chart.js'
import { Line } from 'react-chartjs-2'
import type { SessionStats } from '@/types'
import moment from 'moment'
import { toCompactDuration, isMobile, formatCompactDate } from '@/utils/time'
import 'chartjs-plugin-zoom'

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, TimeScale, Filler)

type Period = 'week' | 'month' | 'year' | 'all'

interface TrendsChartProps {
  allSessions: SessionStats[]
  period: Period
  onPeriodChange: (period: Period) => void
}

export default function TrendsChart({ allSessions, period, onPeriodChange }: TrendsChartProps) {
  const chartRef = useRef<ChartJS<'line'>>(null)

  // Filter sessions by time period
  const filteredSessions = useMemo(() => {
    if (period === 'all') return allSessions

    const now = moment()
    const cutoff = period === 'week' ? now.clone().subtract(7, 'days')
      : period === 'month' ? now.clone().subtract(30, 'days')
      : now.clone().subtract(365, 'days')

    return allSessions.filter(s =>
      s.drinks.length > 0 && moment(s.drinks[0].finished_at).isAfter(cutoff)
    )
  }, [allSessions, period])

  // Session metadata for tooltips
  const sessionMeta = useMemo(() => {
    return filteredSessions.map(s => {
      const first = new Date(s.drinks[0].finished_at).getTime()
      const last = new Date(s.drinks[s.drinks.length - 1].finished_at).getTime()
      const durationSecs = Math.max(0, (last - first) / 1000)
      return {
        drinkCount: s.drinkCount,
        duration: toCompactDuration(durationSecs),
      }
    })
  }, [filteredSessions])

  // Prepare chart data — two series: peak BAC + drink count
  const chartData = useMemo(() => {
    const mobile = isMobile()
    const dateFormat = period === 'year' || period === 'all' ? 'D MMM \'YY' : 'ddd D MMM'
    return {
      labels: filteredSessions.map(s =>
        mobile ? formatCompactDate(s.drinks[0].finished_at) : moment(s.drinks[0].finished_at).format(dateFormat)
      ),
      datasets: [
        {
          label: 'Peak BAC',
          data: filteredSessions.map(s => s.peakBac),
          borderColor: 'rgb(45, 212, 191)',
          backgroundColor: 'rgba(45, 212, 191, 0.1)',
          fill: true,
          tension: 0.3,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: 'rgb(45, 212, 191)',
          yAxisID: 'yBac',
        },
        {
          label: 'Drinks',
          data: filteredSessions.map(s => s.drinkCount),
          borderColor: 'rgb(245, 158, 11)',
          backgroundColor: 'rgba(245, 158, 11, 0.05)',
          fill: true,
          tension: 0.3,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: 'rgb(245, 158, 11)',
          yAxisID: 'yDrinks',
        },
      ],
    }
  }, [filteredSessions, period])

  // Chart options with dual y-axes
  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: {
          color: 'rgb(148, 163, 184)',
          boxWidth: 12,
          padding: 16,
          font: { size: 11 },
        },
      },
      tooltip: {
        backgroundColor: 'rgb(30, 41, 59)',
        titleColor: 'rgb(248, 250, 252)',
        bodyColor: 'rgb(148, 163, 184)',
        borderColor: 'rgb(71, 85, 105)',
        borderWidth: 1,
        callbacks: {
          afterBody: (contexts: { dataIndex: number }[]) => {
            const meta = sessionMeta[contexts[0]?.dataIndex]
            return meta ? `Duration: ${meta.duration}` : ''
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        ticks: {
          color: 'rgb(148, 163, 184)',
          maxRotation: 45,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 10,
        },
        grid: {
          color: 'rgba(71, 85, 105, 0.3)',
        },
      },
      yBac: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        beginAtZero: true,
        title: {
          display: true,
          text: 'BAC %',
          color: 'rgb(45, 212, 191)',
          font: { size: 11 },
        },
        ticks: {
          color: 'rgb(45, 212, 191)',
          callback: (value: number | string) => `${Number(value).toFixed(2)}%`,
        },
        grid: {
          color: 'rgba(71, 85, 105, 0.3)',
        },
      },
      yDrinks: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Drinks',
          color: 'rgb(245, 158, 11)',
          font: { size: 11 },
        },
        ticks: {
          color: 'rgb(245, 158, 11)',
          stepSize: 1,
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  }), [sessionMeta])

  // Reset zoom on period change
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.resetZoom?.()
    }
  }, [period])

  if (allSessions.length === 0) {
    return (
      <div className="card">
        <div className="flex items-center justify-center h-48 text-[var(--text-secondary)]">
          No data to display
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      {/* Period Selector */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
          Session History
        </h3>
        <div className="flex gap-1">
          {(['week', 'month', 'year', 'all'] as const).map((p) => (
            <button
              key={p}
              onClick={() => onPeriodChange(p)}
              className={`px-3 py-1 text-sm rounded transition-colors ${
                period === p
                  ? 'bg-[var(--accent-teal)] text-[var(--bg-primary)]'
                  : 'bg-[var(--bg-card)] text-[var(--text-secondary)] hover:bg-[var(--border)]'
              }`}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div className="h-64">
        {filteredSessions.length > 0 ? (
          <Line ref={chartRef} data={chartData} options={options} />
        ) : (
          <div className="flex items-center justify-center h-full text-[var(--text-secondary)]">
            No sessions in this period
          </div>
        )}
      </div>
    </div>
  )
}
